import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "@/common/providers/i18n-provider";
import { LocaleLink } from "@/components/locale-link";
import { StartFreeLink } from "@/components/cta-buttons";
import {
  DEFAULT_COUNTER_ACCOUNT,
  DEFAULT_CURRENCY,
  DEFAULT_SOURCE_ACCOUNT,
  detectMapping,
  emitBeancount,
  isValidAccount,
  isValidCommodity,
  parseCsv,
  prepareCsvRows,
  type ColumnMapping,
  type CsvOutputMode,
  type PreparedCsvRow,
} from "@/lib/csv-to-beancount";
import {
  MAX_CSV_DESCRIPTION_RULE_PATTERN_LENGTH,
  MAX_CSV_DESCRIPTION_RULES,
  type CsvDescriptionRule,
  type CsvDescriptionRuleOperator,
} from "@/lib/csv-description-rules";
import { compareCsvOverlap } from "@/lib/csv-overlap";
import { type DateOrder } from "@/lib/import-normalize";
import {
  trackCsvCategoryAction,
  trackCsvSplitAction,
  trackToolUse,
  type ToolUseDetails,
} from "@/lib/tools-analytics";
import { HorizontalScrollRegion } from "@/components/horizontal-scroll-region";
import {
  CSV_PROFILE_FIELDS,
  resolveProfileMapping,
  type CsvMappingProfile,
} from "@/lib/csv-mapping-profiles";
import { ConverterOutput, PasteOrUploadInput } from "./converter-ui";
import { CsvOverlapReview } from "./csv-overlap-review";
import { CsvProfileControls } from "./csv-profile-controls";
import {
  CsvSplitReview,
  type CsvSplitDraft,
  type CsvSplitReviewAction,
} from "./csv-split-review";
import { translatedCsvReason } from "./csv-reason-i18n";
import { CsvStatementPanel } from "./csv-statement-panel";
import {
  EMPTY_CSV_STATEMENT_FORM,
  statementOptionsFromForm,
  type CsvStatementFormState,
} from "./csv-statement-form";
import { useConvertTracking } from "./use-convert-tracking";
import type { CsvBankFormat } from "./csv-bank-formats";

/**
 * A realistic two-week export for the "try it with a sample" button: a quoted
 * description containing a comma, a mix of debits and credits, and a header
 * row — the shape of the files readers actually paste. Lives here rather than
 * in the content module, which the eagerly-loaded `*.meta.ts` imports.
 */
const SAMPLE_CSV = `Date,Description,Amount
2026-08-01,"WHOLE FOODS MKT, AUSTIN TX",-84.23
2026-08-02,MONTHLY RENT,-1650.00
2026-08-03,ACME CORP PAYROLL,3200.00
2026-08-05,SPOTIFY USA,-11.99
2026-08-07,TRANSFER TO SAVINGS,-500.00
`;

/**
 * Columns the reader can reassign, in the order they appear in the form.
 * The canonical field list lives in `@/lib/csv-mapping-profiles` (its order
 * is the widget form order); aliasing it here keeps the two from drifting.
 * `category` is a full member now that saved profiles persist it, so the
 * review list below works on every page through this one widget.
 */
const MAPPABLE: ReadonlyArray<keyof ColumnMapping> = CSV_PROFILE_FIELDS;

const PREVIEW_ROWS = 6;

/**
 * How many categorized rows the rule review lists with their per-row
 * reason. Same bound as the raw preview: enough to see precedence and copy
 * an unmatched description into a new rule, never the whole file.
 */
const RULE_PREVIEW_ROWS = 6;

/**
 * Most source categories shown with their own account control. Real exports
 * carry a few dozen at most (Mint ~30); past this the rest still convert
 * through the fallback account and the section says how many are hidden, so
 * the DOM stays bounded on pathological files.
 */
const MAX_CATEGORY_REVIEW = 50;

/** Stable empty split list, so an inactive context does not rebuild options. */
const NO_SPLITS: readonly CsvSplitDraft[] = [];

/**
 * The converter itself — paste box, mapping controls, output panel.
 *
 * Split from the page shell so the bank landers at
 * `/tools/csv-to-beancount/<bank>` can wrap it in their own hero and
 * export-walkthrough copy instead of duplicating the tool. All of the
 * converter's state lives here; the shells around it are presentational.
 *
 * Its labels are `tools.csvWidget.*` — the shared family file — not either
 * page's slice. `namespacesForPath` hands a lander its own slice and never its
 * parent's, so copy mounted on both sides of that boundary has to live in the
 * file every `/tools/*` page loads or a preset lander renders raw keys. The
 * rule and split editors' copy (`toolsCsvRules.*`, `toolsCsvSplit.*`) lives in
 * the `tools-csv-rules` child every CSV page loads, off the `/tools` hub.
 */
export function CsvConverterWidget({
  preset,
  presetSlug,
}: { preset?: CsvBankFormat; presetSlug?: string } = {}) {
  const { t, locale } = useTranslation();

  const [text, setText] = useState("");
  const [priorText, setPriorText] = useState("");
  const [excludedRowIndexes, setExcludedRowIndexes] = useState<Set<number>>(
    () => new Set(),
  );
  const [overrides, setOverrides] = useState<Partial<ColumnMapping>>({});
  const [sourceAccount, setSourceAccount] = useState(DEFAULT_SOURCE_ACCOUNT);
  const [counterAccount, setCounterAccount] = useState(DEFAULT_COUNTER_ACCOUNT);
  // Explicit category-value → destination-account assignments, keyed by the
  // trimmed cell text. Blank values are not passed to the emitter (they read
  // as "fall back"), so typing never blocks conversion halfway.
  const [categoryAccounts, setCategoryAccounts] = useState<
    Record<string, string>
  >({});
  // Ordered literal description → account rules, highest priority first. The rule editor below owns the editing UI and profiles
  // own persistence; this state is the shared live copy the emitter reads.
  // Fully-blank editor drafts are filtered out before conversion (see
  // `effectiveDescriptionRules`), so re-pasting never leaks one file's
  // merchants into the next conversion.
  const [descriptionRules, setDescriptionRules] = useState<
    CsvDescriptionRule[]
  >([]);
  const ruleIdCounter = useRef(0);
  const [currency, setCurrency] = useState(
    preset?.currency ?? DEFAULT_CURRENCY,
  );
  const [invert, setInvert] = useState(preset?.invertAmounts ?? false);
  const [dateOrder, setDateOrder] = useState<DateOrder | undefined>(
    preset?.dateOrder,
  );
  const [outputMode, setOutputMode] = useState<CsvOutputMode>("complete");
  // Statement balances stay in component memory only — never profiles, URLs,
  // analytics payloads or network requests. Disabled until the reader opts in.
  const [statementForm, setStatementForm] = useState<CsvStatementFormState>(
    () => ({ ...EMPTY_CSV_STATEMENT_FORM }),
  );
  // Per-row split edits, held in memory only — never profiles, URLs,
  // analytics payloads or network requests. Stamped with the source context
  // they were made in (see `splitContextKey` below); a different context
  // reads them as absent on the very render it changes, so no edit can reach
  // another file's row or keep a previous artifact alive.
  const [splitState, setSplitState] = useState<{
    context: string;
    drafts: readonly CsvSplitDraft[];
  }>({ context: "", drafts: NO_SPLITS });

  // The textarea stays at input priority while the conversion runs as a
  // transition, so typing into a large paste doesn't block keystrokes — and
  // React abandons superseded runs instead of finishing every one.
  const deferredText = useDeferredValue(text);
  const deferredPriorText = useDeferredValue(priorText);
  const inputPending =
    text !== deferredText || priorText !== deferredPriorText;
  const parsed = useMemo(() => parseCsv(deferredText), [deferredText]);
  // A preset is a mapping someone verified, so detection is not merely
  // overridden but skipped — a wrong guess can't briefly win a render.
  const detected = useMemo(
    () => preset?.mapping ?? detectMapping(parsed.headers, parsed.rows),
    [parsed, preset],
  );
  const mapping = useMemo<ColumnMapping>(
    () => ({ ...detected, ...overrides }),
    [detected, overrides],
  );

  // Assignments the emitter sees: blanks read as "fall back to the counter
  // account" (the emitter would reject an empty account and block the whole
  // run), so only non-blank values cross over.
  const assignedCategoryAccounts = useMemo(() => {
    const assigned: Record<string, string> = {};
    for (const [name, account] of Object.entries(categoryAccounts)) {
      if (account.trim() !== "") assigned[name] = account;
    }
    return assigned;
  }, [categoryAccounts]);

  // Rules the emitter sees: fully-blank editor drafts (a fresh rule starts
  // with an empty pattern and account) never cross over, so clicking Add
  // cannot block conversion by itself. Half-filled drafts do cross over and
  // block with an explicit line-0 error — exactly like a half-filled
  // category assignment — instead of silently matching or falling back.
  const effectiveDescriptionRules = useMemo(
    () =>
      descriptionRules.filter(
        (rule) =>
          !rule.enabled ||
          rule.pattern.trim() !== "" ||
          rule.account.trim() !== "",
      ),
    [descriptionRules],
  );

  // What a split is made *against*: the deferred source text, the column
  // mapping, the sign convention and the source account/currency. Category
  // assignments, rules and the counter account are deliberately absent — a
  // split replaces the other leg, so re-mapping a category keeps it. Derived
  // from the deferred text so an edit during a pending paste cannot attach
  // to a snapshot that is about to be replaced.
  const splitContextKey = useMemo(
    () =>
      JSON.stringify({
        text: deferredText,
        mapping,
        sourceAccount,
        currency,
        invert,
        dateOrder,
        signColumn: preset?.signColumn ?? null,
        debitValues: preset?.debitValues ?? null,
      }),
    [deferredText, mapping, sourceAccount, currency, invert, dateOrder, preset],
  );
  const splitDrafts =
    splitState.context === splitContextKey ? splitState.drafts : NO_SPLITS;
  // The comparison above hides stale drafts on the render the context
  // changes; this drops them for good (React's adjust-state-during-render
  // pattern), so flipping a setting away and back (A → B → A) cannot
  // resurrect edits made under A.
  if (splitState.context !== splitContextKey && splitState.drafts.length > 0) {
    setSplitState({ context: splitContextKey, drafts: NO_SPLITS });
  }
  // The one write path for split edits, and the only split telemetry hook
  // besides the takeaway below: an explicit start/reset/remove click carries
  // its `action`; allocation keystrokes and add/remove-line clicks carry
  // none, and recalculation never passes through here at all.
  function changeSplitDrafts(
    next: readonly CsvSplitDraft[],
    action?: CsvSplitReviewAction,
  ) {
    setSplitState({ context: splitContextKey, drafts: next });
    if (action !== undefined) trackCsvSplitAction(action, presetSlug);
  }

  const convertOptions = useMemo(() => {
    // Any mapping / selection / statement-field change rebuilds this object,
    // so emitBeancount recomputes reconciliation and drops a stale matched
    // export. Deferred CSV text is already gated by `inputPending` below.
    const statement = statementOptionsFromForm(statementForm, currency);
    return {
      sourceAccount,
      counterAccount,
      categoryAccounts: assignedCategoryAccounts,
      descriptionRules: effectiveDescriptionRules,
      currency,
      invertAmounts: invert,
      // A sign *rule*, not a column assignment, so it stays preset-supplied
      // rather than becoming another select: the reader has no decision to
      // make about which value in Mint's `Transaction Type` means "out".
      signColumn: preset?.signColumn,
      debitValues: preset?.debitValues,
      dateOrder,
      outputMode,
      excludeRowIndexes: excludedRowIndexes,
      ...(statement ? { statement } : {}),
      // Omitted (not `[]`) with no active split, so unsplit output and
      // result shape stay exactly what they were before splits existed.
      ...(splitDrafts.length > 0 ? { rowSplits: splitDrafts } : {}),
    };
  }, [
    sourceAccount,
    counterAccount,
    assignedCategoryAccounts,
    effectiveDescriptionRules,
    currency,
    invert,
    preset,
    dateOrder,
    outputMode,
    excludedRowIndexes,
    statementForm,
    splitDrafts,
  ]);

  // Unique source categories in the mapped column with their row counts, in
  // a stable order (most rows first, then by name). `null` while no category
  // column is mapped — the review section stays hidden, not empty. Counts
  // follow the current inclusion set so excluded rows do not inflate the
  // mapped/fallback totals next to the artifact.
  const categoryBreakdown = useMemo(() => {
    const column = mapping.category ?? null;
    if (column === null) return null;
    const counts = new Map<string, number>();
    let blank = 0;
    parsed.rows.forEach((row, rowIndex) => {
      if (excludedRowIndexes.has(rowIndex)) return;
      const value = (row[column] ?? "").trim();
      if (value === "") blank += 1;
      else counts.set(value, (counts.get(value) ?? 0) + 1);
    });
    const ranked = [...counts.entries()].sort(
      ([nameA, countA], [nameB, countB]) =>
        countB - countA || (nameA < nameB ? -1 : nameA > nameB ? 1 : 0),
    );
    return { ranked, blank };
  }, [parsed.rows, mapping, excludedRowIndexes]);

  const result = useMemo(
    () =>
      emitBeancount(
        parsed.rows,
        mapping,
        convertOptions,
        parsed.headers !== null,
      ),
    [parsed, mapping, convertOptions],
  );

  const overlapComparison = useMemo(() => {
    if (deferredPriorText.trim() === "" || parsed.rows.length === 0) {
      return null;
    }
    return compareCsvOverlap({
      current: parsed,
      priorText: deferredPriorText,
      mapping,
      options: {
        sourceAccount,
        counterAccount,
        categoryAccounts: assignedCategoryAccounts,
        descriptionRules: effectiveDescriptionRules,
        currency,
        invertAmounts: invert,
        signColumn: preset?.signColumn,
        debitValues: preset?.debitValues,
        dateOrder,
      },
    });
  }, [
    deferredPriorText,
    parsed,
    mapping,
    sourceAccount,
    counterAccount,
    assignedCategoryAccounts,
    effectiveDescriptionRules,
    currency,
    invert,
    preset,
    dateOrder,
  ]);

  // Drop stale exclusions when the comparison inputs change. Selection is by
  // current row index, so a new CSV or mapping must not reuse old decisions.
  const exclusionContextKey = useMemo(
    () =>
      JSON.stringify({
        text: deferredText,
        prior: deferredPriorText,
        mapping,
        sourceAccount,
        counterAccount,
        currency,
        invert,
        dateOrder,
        signColumn: preset?.signColumn ?? null,
        debitValues: preset?.debitValues ?? null,
        categoryAccounts: assignedCategoryAccounts,
        descriptionRules: effectiveDescriptionRules,
      }),
    [
      deferredText,
      deferredPriorText,
      mapping,
      sourceAccount,
      counterAccount,
      currency,
      invert,
      dateOrder,
      preset,
      assignedCategoryAccounts,
      effectiveDescriptionRules,
    ],
  );
  const lastExclusionContext = useRef(exclusionContextKey);
  useEffect(() => {
    if (lastExclusionContext.current === exclusionContextKey) return;
    lastExclusionContext.current = exclusionContextKey;
    setExcludedRowIndexes(new Set());
  }, [exclusionContextKey]);

  // Row counts are data rows without the header: `parsed.rows` already drops
  // the header row and blank lines, so input/converted/skipped share one base.
  const inputCount = parsed.rows.length;
  const skippedCount = result.errors.filter((error) => error.line > 0).length;
  const blockingErrors = result.errors.filter((error) => error.line === 0);
  const rowErrors = result.errors.filter((error) => error.line > 0);
  const resultState: ToolUseDetails["result"] =
    blockingErrors.length > 0
      ? "blocked"
      : result.converted === 0
        ? "empty"
        : skippedCount > 0
          ? "partial"
          : "complete";
  const trackingDetails: ToolUseDetails = {
    outputMode: result.outputMode,
    result: resultState,
  };
  const filename =
    result.outputMode === "complete" ? "ledger.bean" : "transactions.bean";
  // A selected row whose split does not validate blocks every artifact
  // action until it is fixed, removed or the row is excluded — the emitter
  // already dropped the row, and a ledger silently missing it is worse than
  // none. Excluded rows never report diagnostics, so they never block.
  const splitBlocked =
    !inputPending && (result.splitDiagnostics?.length ?? 0) > 0;
  // Pending deferred input must not offer a download of the previous artifact
  // — including a stale matched statement ledger from the prior snapshot.
  const outputReady = !inputPending && !splitBlocked && result.converted > 0;
  // The artifact carries at least one applied split: output is ready (so
  // every selected split validated) and a split row is still selected.
  const splitInOutput =
    outputReady &&
    splitDrafts.some((draft) => !excludedRowIndexes.has(draft.rowIndex));
  const outputText = outputReady ? result.text : "";
  const outputConverted = outputReady ? result.converted : 0;
  const statementReconciled =
    outputReady && result.statementReconciled === true;

  useConvertTracking(
    "csv_to_beancount",
    deferredText,
    outputConverted,
    presetSlug,
    outputConverted > 0 ? trackingDetails : undefined,
  );

  /**
   * Apply a saved mapping profile after an explicit Load click. Only the
   * configuration moves — the pasted transactions in `text` are never
   * touched, so loading cannot replace what the reader pasted. Columns
   * resolve by header name with no index fallback (missing or duplicated
   * names land unmapped for explicit remapping, the category column
   * included), and a profile saved under another preset never flips this
   * export's sign convention: its `invertAmounts` stays for the reader to
   * review. Saved category assignments restore as-is; names this file does
   * not carry sit unused until a file does, and the review list below shows
   * exactly where every row posts.
   */
  function applyProfile(profile: CsvMappingProfile) {
    // The controls already ran the full compatibility check for their
    // notice; the only verdict this side needs is the preset comparison.
    const presetMismatch =
      (profile.presetSlug ?? null) !== (presetSlug ?? null);
    setOverrides(resolveProfileMapping(profile, parsed.headers));
    setSourceAccount(profile.config.sourceAccount);
    setCounterAccount(profile.config.counterAccount);
    // Blanks never persist, so everything here is an explicit account choice.
    setCategoryAccounts(profile.config.categoryAccounts ?? {});
    // A missing field means "no rules" (profiles saved before rules
    // existed): reset rather than keep whatever the reader had active, so a
    // rule-free Load never inherits stale rules.
    setDescriptionRules(profile.config.descriptionRules ?? []);
    setCurrency(profile.config.currency);
    if (!presetMismatch) {
      setInvert(profile.config.invertAmounts);
    }
    setDateOrder(profile.config.dateOrder);
    setOutputMode(profile.config.outputMode);
    // Profiles never carry statement balances. Clear the panel so a Load
    // cannot keep a prior match against a newly restored mapping.
    setStatementForm({ ...EMPTY_CSV_STATEMENT_FORM });
  }

  function load(next: string) {
    // A new input replaces the CSV, not the reusable configuration: column
    // overrides reset (indexes belong to the old file), while the source and
    // counter accounts, category assignments and description rules persist —
    // exactly what a saved profile restores. Every reason and count
    // recomputes from the deferred preparation, so no preview or ledger can
    // read as the old file's; overlap exclusions reset with the comparison
    // inputs through `exclusionContextKey` on the next render.
    setText(next);
    setOverrides({});
    // Back to the preset's declared order, not to "no order at all": a new
    // input drops the reader's correction, but a bank whose export is
    // day-first is still day-first. Clearing it outright made every Wise paste
    // read `01-08-2026` as 8 January — the column has nothing over 12 and no
    // dotted separator, so detection can only call it ambiguous and assume
    // month-first. On the generic converter there is no preset and this stays
    // `undefined`, i.e. detect from the column.
    setDateOrder(preset?.dateOrder);
    // Sample / paste / upload replaces the CSV — drop statement context so a
    // Try sample cannot keep balances that matched the previous paste.
    setStatementForm({ ...EMPTY_CSV_STATEMENT_FORM });
  }

  function clearPriorHistory() {
    setPriorText("");
    setExcludedRowIndexes(new Set());
  }

  /**
   * Description-rule editing. Every control is a native checkbox, select,
   * input or button, so the whole editor is keyboard-operable with no extra
   * wiring. Array position is the priority (first enabled match wins); ids
   * stay stable across reorders so `matchedRuleId` on prepared rows keeps
   * pointing at the same rule.
   */
  function addDescriptionRule() {
    ruleIdCounter.current += 1;
    const id = `rule-${ruleIdCounter.current}`;
    setDescriptionRules((prev) =>
      prev.length >= MAX_CSV_DESCRIPTION_RULES
        ? prev
        : [
            ...prev,
            {
              id,
              enabled: true,
              operator: "contains",
              pattern: "",
              account: "",
            } satisfies CsvDescriptionRule,
          ],
    );
  }

  function updateDescriptionRule(
    id: string,
    patch: Partial<
      Pick<CsvDescriptionRule, "enabled" | "operator" | "pattern" | "account">
    >,
  ) {
    setDescriptionRules((prev) =>
      prev.map((rule) => (rule.id === id ? { ...rule, ...patch } : rule)),
    );
  }

  function removeDescriptionRule(id: string) {
    setDescriptionRules((prev) => prev.filter((rule) => rule.id !== id));
  }

  function moveDescriptionRule(id: string, delta: -1 | 1) {
    setDescriptionRules((prev) => {
      const from = prev.findIndex((rule) => rule.id === id);
      const to = from + delta;
      if (from === -1 || to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      return next;
    });
  }

  const columnOptions = useMemo(() => {
    return Array.from({ length: parsed.width }, (_, i) => ({
      value: i,
      label:
        parsed.headers?.[i]?.trim() ||
        t("tools.csvWidget.mapping.column", {
          n: (i + 1).toLocaleString(locale),
        }),
    }));
  }, [parsed, t, locale]);

  // The converter already resolved the order; asking again here would mean a
  // second scan of the date column and a second definition of "ambiguous".
  // Category review, derived per render from the live rows: rows whose
  // category carries a non-blank assignment post to it, everything else —
  // blank assignments, unlisted categories, empty cells — falls back to the
  // counter account, exactly like the emitter one memo above.
  const categoryReview =
    parsed.rows.length > 0 && categoryBreakdown !== null
      ? categoryBreakdown
      : null;
  let mappedCategoryRows = 0;
  let fallbackCategoryRows = 0;
  if (categoryReview !== null) {
    for (const [name, count] of categoryReview.ranked) {
      if (assignedCategoryAccounts[name] !== undefined) {
        mappedCategoryRows += count;
      } else {
        fallbackCategoryRows += count;
      }
    }
    fallbackCategoryRows += categoryReview.blank;
  }
  const visibleCategories =
    categoryReview?.ranked.slice(0, MAX_CATEGORY_REVIEW) ?? [];
  const hiddenCategoryCount =
    (categoryReview?.ranked.length ?? 0) - visibleCategories.length;

  // Row-level account reasons from the same deferred preparation the
  // emitter runs: same rows, mapping and live options, minus exclusions
  // (which only omit rows from the artifact, like the category counts
  // above). `null` while deferred input is pending or configuration blocks
  // conversion, so the counts and reasons never show a stale success — the
  // pending note and the line-0 error list own those states instead.
  const preparedReview = useMemo(() => {
    if (parsed.rows.length === 0 || inputPending) return null;
    const prepared = prepareCsvRows(
      parsed.rows,
      mapping,
      convertOptions,
      parsed.headers !== null,
    );
    return prepared.blockingErrors.length > 0 ? null : prepared;
  }, [parsed, mapping, convertOptions, inputPending]);
  // Every convertible row, exclusions included: the split picker offers the
  // current file's rows, and an excluded row's split stays editable (inert).
  const splitRows = useMemo(
    () =>
      preparedReview?.results.flatMap((entry) =>
        entry.ok ? [entry.row] : [],
      ) ?? [],
    [preparedReview],
  );
  // The preview reads the same split state the emitter applied: a valid
  // split lists its destinations, a selected row whose split does not
  // validate says so — never the counter account it will not post to — and
  // neither counts toward the rule/fallback summary.
  const failedSplitRows = useMemo(
    () =>
      new Set(
        (result.splitDiagnostics ?? []).map((diagnostic) => diagnostic.rowIndex),
      ),
    [result.splitDiagnostics],
  );
  const ruleReview = useMemo(() => {
    if (preparedReview === null) return null;
    let categoryRows = 0;
    let matchedRows = 0;
    let fallbackRows = 0;
    const rows: PreparedCsvRow[] = [];
    for (const entry of preparedReview.results) {
      if (!entry.ok) continue;
      if (excludedRowIndexes.has(entry.row.rowIndex)) continue;
      rows.push(entry.row);
      if (
        entry.row.split !== undefined ||
        failedSplitRows.has(entry.row.rowIndex)
      ) {
        continue;
      }
      if (entry.row.accountSource === "category") categoryRows += 1;
      else if (entry.row.accountSource === "rule") matchedRows += 1;
      else fallbackRows += 1;
    }
    return { categoryRows, matchedRows, fallbackRows, rows };
  }, [preparedReview, excludedRowIndexes, failedSplitRows]);
  const ruleById = useMemo(
    () => new Map(descriptionRules.map((rule) => [rule.id, rule])),
    [descriptionRules],
  );
  function accountSourceLabel(row: PreparedCsvRow): string {
    if (row.accountSource === "category") {
      return t("toolsCsvRules.reason.category");
    }
    if (row.accountSource === "rule") {
      return t("toolsCsvRules.reason.rule", {
        pattern: ruleById.get(row.matchedRuleId ?? "")?.pattern.trim() ?? "",
      });
    }
    return t("toolsCsvRules.reason.fallback");
  }
  const visibleRuleRows = ruleReview?.rows.slice(0, RULE_PREVIEW_ROWS) ?? [];
  const hiddenRuleRowCount =
    (ruleReview?.rows.length ?? 0) - visibleRuleRows.length;
  const rulesAtMax = descriptionRules.length >= MAX_CSV_DESCRIPTION_RULES;

  // Category-review outcome for measurement: one bounded `csv_category`
  // event per distinct converted input *and* outcome. Pasting a categorized
  // export emits `fallback` while rows still post to the counter account;
  // assigning the last fallback flips a later emission to `mapped` for the
  // same input — so the pair shows whether review resolves fallbacks. Only
  // the outcome enum (plus the preset slug) travels: never category values,
  // account names, amounts or headers. No category column, or nothing
  // converted, emits nothing.
  const categoryOutcome =
    categoryReview !== null && result.converted > 0
      ? fallbackCategoryRows > 0
        ? "fallback"
        : "mapped"
      : null;
  const trackedCategoryRef = useRef<{
    input: string;
    outcome: "fallback" | "mapped";
  } | null>(null);
  useEffect(() => {
    if (categoryOutcome === null) return;
    const last = trackedCategoryRef.current;
    if (
      last !== null &&
      last.input === deferredText &&
      last.outcome === categoryOutcome
    ) {
      return;
    }
    trackedCategoryRef.current = {
      input: deferredText,
      outcome: categoryOutcome,
    };
    trackCsvCategoryAction(categoryOutcome, presetSlug);
  }, [categoryOutcome, deferredText, presetSlug]);

  const ambiguousDates = result.dateOrder === "ambiguous";
  const hasInput = parsed.rows.length > 0;
  const sourceValid = isValidAccount(sourceAccount);
  const counterValid = isValidAccount(counterAccount);
  const currencyValid = isValidCommodity(currency.trim() || DEFAULT_CURRENCY);
  const ctaSurface = presetSlug ? "tools_csv_bank" : "tools_csv";
  const allExcluded =
    !inputPending &&
    hasInput &&
    result.converted === 0 &&
    result.excluded > 0 &&
    blockingErrors.length === 0;

  return (
    <section id="converter" className="company-container tool-workspace">
      <div className="tool-workspace-grid">
        <div className="tool-input-pane">
          <PasteOrUploadInput
            id="csv-input"
            keyPrefix="tools.csvWidget"
            accept=".csv,text/csv,text/plain"
            value={text}
            onChange={load}
            sample={preset?.sampleCsv ?? SAMPLE_CSV}
            rows={12}
            outputAvailable={outputReady}
          />
          <CsvProfileControls
            headers={parsed.headers}
            presetSlug={presetSlug ?? null}
            settings={{
              mapping,
              sourceAccount,
              counterAccount,
              currency,
              invertAmounts: invert,
              dateOrder,
              outputMode,
              // Already blank-filtered above: blanks read as "fall back" and
              // never reach the snapshot.
              categoryAccounts: assignedCategoryAccounts,
              // Saved on explicit Save/Update only — never analytics or URLs.
              descriptionRules,
            }}
            onApply={applyProfile}
          />
          {hasInput && (
            <CsvOverlapReview
              priorText={priorText}
              onPriorChange={(value) => {
                if (value === "") clearPriorHistory();
                else setPriorText(value);
              }}
              comparison={overlapComparison}
              excludedRowIndexes={excludedRowIndexes}
              onExcludedChange={setExcludedRowIndexes}
            />
          )}
          {hasInput && (
            <>
              <h2 className="tool-settings-heading">
                {t("tools.csvWidget.mapping.title")}
              </h2>
              <div className="tool-settings-grid">
                {MAPPABLE.map((field) => (
                  <label key={field} className="tool-control-label">
                    <span className="tool-label-text">
                      {t(`tools.csvWidget.mapping.${field}`)}
                    </span>
                    <select
                      value={mapping[field] ?? ""}
                      onChange={(event) =>
                        setOverrides((prev) => ({
                          ...prev,
                          [field]:
                            event.target.value === ""
                              ? null
                              : Number(event.target.value),
                        }))
                      }
                      className="tool-control"
                    >
                      <option value="">
                        {t("tools.csvWidget.mapping.none")}
                      </option>
                      {columnOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>

              <h2 className="tool-settings-heading">
                {t("tools.csvWidget.options.title")}
              </h2>
              <div className="tool-settings-grid">
                <label className="tool-control-label">
                  <span className="tool-label-text">
                    {t("tools.csvWidget.options.sourceAccount")}
                  </span>
                  <input
                    value={sourceAccount}
                    onChange={(event) => setSourceAccount(event.target.value)}
                    spellCheck={false}
                    dir="ltr"
                    aria-invalid={!sourceValid}
                    className="tool-control tool-account"
                  />
                </label>
                <label className="tool-control-label">
                  <span className="tool-label-text">
                    {t("tools.csvWidget.options.counterAccount")}
                  </span>
                  <input
                    value={counterAccount}
                    onChange={(event) => setCounterAccount(event.target.value)}
                    spellCheck={false}
                    dir="ltr"
                    aria-invalid={!counterValid}
                    className="tool-control tool-account"
                  />
                </label>
                <label className="tool-control-label">
                  <span className="tool-label-text">
                    {t("tools.csvWidget.options.currency")}
                  </span>
                  <input
                    value={currency}
                    onChange={(event) =>
                      setCurrency(event.target.value.toUpperCase())
                    }
                    spellCheck={false}
                    dir="ltr"
                    aria-invalid={!currencyValid}
                    className="tool-control tool-account"
                  />
                </label>
                {ambiguousDates && (
                  <label className="tool-control-label">
                    <span className="tool-label-text">
                      {t("tools.csvWidget.options.dateOrder")}
                    </span>
                    <select
                      value={dateOrder ?? "mdy"}
                      onChange={(event) =>
                        setDateOrder(event.target.value as DateOrder)
                      }
                      className="tool-control"
                    >
                      <option value="mdy">
                        {t("tools.csvWidget.options.mdy")}
                      </option>
                      <option value="dmy">
                        {t("tools.csvWidget.options.dmy")}
                      </option>
                    </select>
                  </label>
                )}
              </div>
              <label className="tool-checkbox">
                <input
                  type="checkbox"
                  checked={invert}
                  onChange={(event) => setInvert(event.target.checked)}
                  className="mt-1"
                />
                <span>
                  <span className="tool-label-text">
                    {t("tools.csvWidget.options.invert")}
                  </span>{" "}
                  {t("tools.csvWidget.options.invertHint")}
                </span>
              </label>

              {categoryReview !== null && (
                <>
                  <h2 className="tool-settings-heading">
                    {t("tools.csvWidget.categories.title")}
                  </h2>
                  <p className="tool-local-note">
                    {t("tools.csvWidget.categories.description")}
                  </p>
                  <p className="tool-status" role="status">
                    {t("tools.csvWidget.categories.summary", {
                      mapped: mappedCategoryRows.toLocaleString(locale),
                      fallback: fallbackCategoryRows.toLocaleString(locale),
                      account:
                        counterAccount.trim() || DEFAULT_COUNTER_ACCOUNT,
                    })}
                  </p>
                  <div className="tool-settings-grid">
                    {visibleCategories.map(([name, count]) => {
                      const value = categoryAccounts[name] ?? "";
                      return (
                        <label key={name} className="tool-control-label">
                          <span className="tool-label-text">
                            <span dir="auto">{name}</span>{" "}
                            {t("tools.csvWidget.categories.rows", {
                              count: count.toLocaleString(locale),
                            })}
                          </span>
                          <input
                            value={value}
                            onChange={(event) =>
                              setCategoryAccounts((prev) => ({
                                ...prev,
                                [name]: event.target.value,
                              }))
                            }
                            placeholder={counterAccount}
                            spellCheck={false}
                            dir="ltr"
                            aria-invalid={
                              value !== "" && !isValidAccount(value)
                            }
                            className="tool-control tool-account"
                          />
                        </label>
                      );
                    })}
                  </div>
                  {categoryReview.blank > 0 && (
                    <p className="tool-local-note">
                      {t("tools.csvWidget.categories.empty", {
                        count: categoryReview.blank.toLocaleString(locale),
                      })}
                    </p>
                  )}
                  {hiddenCategoryCount > 0 && (
                    <p className="tool-local-note">
                      {t("tools.csvWidget.categories.more", {
                        count: hiddenCategoryCount.toLocaleString(locale),
                      })}
                    </p>
                  )}
                </>
              )}

              <>
                <h2 className="tool-settings-heading">
                  {t("toolsCsvRules.title")}
                </h2>
                <p className="tool-local-note">
                  {t("toolsCsvRules.description")}
                </p>
                {ruleReview !== null && (
                  <p className="tool-status" role="status">
                    {t("toolsCsvRules.summary", {
                      matched: ruleReview.matchedRows.toLocaleString(locale),
                      fallback: ruleReview.fallbackRows.toLocaleString(locale),
                      account:
                        counterAccount.trim() || DEFAULT_COUNTER_ACCOUNT,
                    })}
                  </p>
                )}
                {descriptionRules.length === 0 ? (
                  <p className="tool-local-note">
                    {t("toolsCsvRules.empty")}
                  </p>
                ) : (
                  <ul>
                    {descriptionRules.map((rule, index) => {
                      const ruleNumber = (index + 1).toLocaleString(locale);
                      const ruleLabel = t("toolsCsvRules.rule", {
                        n: ruleNumber,
                      });
                      const patternBlank = rule.pattern.trim() === "";
                      const accountBlank = rule.account.trim() === "";
                      // A fully-blank draft is inert (filtered before
                      // conversion), so only a half-filled or overlong
                      // pattern on an enabled rule reads as an error.
                      const patternInvalid =
                        rule.enabled &&
                        ((patternBlank && !accountBlank) ||
                          (!patternBlank &&
                            rule.pattern.trim().length >
                              MAX_CSV_DESCRIPTION_RULE_PATTERN_LENGTH));
                      return (
                        <li key={rule.id}>
                          <div role="group" aria-label={ruleLabel}>
                            <p className="tool-label-text" aria-hidden="true">
                              {ruleLabel}
                            </p>
                            <div className="tool-settings-grid">
                              <label className="tool-checkbox">
                                <input
                                  type="checkbox"
                                  checked={rule.enabled}
                                  onChange={(event) =>
                                    updateDescriptionRule(rule.id, {
                                      enabled: event.target.checked,
                                    })
                                  }
                                  className="mt-1"
                                />
                                <span className="tool-label-text">
                                  {t("toolsCsvRules.enabled")}
                                </span>
                              </label>
                              <label className="tool-control-label">
                                <span className="tool-label-text">
                                  {t("toolsCsvRules.operator")}
                                </span>
                                <select
                                  value={rule.operator}
                                  onChange={(event) =>
                                    updateDescriptionRule(rule.id, {
                                      operator: event.target
                                        .value as CsvDescriptionRuleOperator,
                                    })
                                  }
                                  className="tool-control"
                                >
                                  <option value="contains">
                                    {t("toolsCsvRules.contains")}
                                  </option>
                                  <option value="equals">
                                    {t("toolsCsvRules.equals")}
                                  </option>
                                </select>
                              </label>
                              <label className="tool-control-label">
                                <span className="tool-label-text">
                                  {t("toolsCsvRules.pattern")}
                                </span>
                                <input
                                  value={rule.pattern}
                                  onChange={(event) =>
                                    updateDescriptionRule(rule.id, {
                                      pattern: event.target.value,
                                    })
                                  }
                                  placeholder={t(
                                    "toolsCsvRules.patternPlaceholder",
                                  )}
                                  spellCheck={false}
                                  dir="auto"
                                  aria-invalid={patternInvalid}
                                  className="tool-control"
                                />
                              </label>
                              <label className="tool-control-label">
                                <span className="tool-label-text">
                                  {t("toolsCsvRules.account")}
                                </span>
                                <input
                                  value={rule.account}
                                  onChange={(event) =>
                                    updateDescriptionRule(rule.id, {
                                      account: event.target.value,
                                    })
                                  }
                                  placeholder={counterAccount}
                                  spellCheck={false}
                                  dir="ltr"
                                  aria-invalid={
                                    !accountBlank &&
                                    !isValidAccount(rule.account.trim())
                                  }
                                  className="tool-control tool-account"
                                />
                              </label>
                            </div>
                            <div className="tool-file-actions">
                              <button
                                type="button"
                                onClick={() =>
                                  moveDescriptionRule(rule.id, -1)
                                }
                                disabled={index === 0}
                              >
                                {t("toolsCsvRules.moveUp")}
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  moveDescriptionRule(rule.id, 1)
                                }
                                disabled={
                                  index === descriptionRules.length - 1
                                }
                              >
                                {t("toolsCsvRules.moveDown")}
                              </button>
                              <button
                                type="button"
                                onClick={() => removeDescriptionRule(rule.id)}
                              >
                                {t("toolsCsvRules.remove")}
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {rulesAtMax ? (
                  <p className="tool-local-note">
                    {t("toolsCsvRules.maxed", {
                      max: MAX_CSV_DESCRIPTION_RULES.toLocaleString(locale),
                    })}
                  </p>
                ) : (
                  <div className="tool-file-actions">
                    <button type="button" onClick={addDescriptionRule}>
                      {t("toolsCsvRules.add")}
                    </button>
                  </div>
                )}
                {ruleReview !== null && visibleRuleRows.length > 0 && (
                  <>
                    <h3 className="tool-settings-heading">
                      {t("toolsCsvRules.matches.title")}
                    </h3>
                    <ul className="tool-notes-list">
                      {visibleRuleRows.map((row) => (
                        <li key={row.rowIndex}>
                          <span dir="auto">
                            {row.description === ""
                              ? t("toolsCsvRules.blankDescription")
                              : row.description}
                          </span>
                          {": "}
                          {failedSplitRows.has(row.rowIndex) ? (
                            <span>{t("toolsCsvSplit.rowError")}</span>
                          ) : row.split !== undefined ? (
                            <>
                              <span dir="ltr">
                                {row.split.allocations
                                  .map((allocation) => allocation.account)
                                  .join(", ")}
                              </span>{" "}
                              <span>
                                (
                                {t("toolsCsvSplit.previewReason", {
                                  count:
                                    row.split.allocations.length.toLocaleString(
                                      locale,
                                    ),
                                })}
                                )
                              </span>
                            </>
                          ) : (
                            <>
                              <span dir="ltr">{row.counterAccount}</span>{" "}
                              <span>({accountSourceLabel(row)})</span>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                    {hiddenRuleRowCount > 0 && (
                      <p className="tool-local-note">
                        {t("toolsCsvRules.matches.more", {
                          count: hiddenRuleRowCount.toLocaleString(locale),
                        })}
                      </p>
                    )}
                  </>
                )}
              </>

              <CsvSplitReview
                rows={splitRows}
                drafts={splitDrafts}
                onDraftsChange={changeSplitDrafts}
                diagnostics={result.splitDiagnostics}
                excludedRowIndexes={excludedRowIndexes}
                counterAccount={counterAccount}
                pending={inputPending}
              />

              <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
                <legend className="tool-settings-heading">
                  {t("tools.csvWidget.mode.title")}
                </legend>
                <label className="tool-checkbox">
                  <input
                    type="radio"
                    name="csv-output-mode"
                    value="complete"
                    checked={outputMode === "complete"}
                    onChange={() => setOutputMode("complete")}
                    className="mt-1"
                  />
                  <span>
                    <span className="tool-label-text">
                      {t("tools.csvWidget.mode.complete")}
                    </span>{" "}
                    {t("tools.csvWidget.mode.completeHint")}
                  </span>
                </label>
                <label className="tool-checkbox">
                  <input
                    type="radio"
                    name="csv-output-mode"
                    value="append"
                    checked={outputMode === "append"}
                    onChange={() => setOutputMode("append")}
                    className="mt-1"
                  />
                  <span>
                    <span className="tool-label-text">
                      {t("tools.csvWidget.mode.append")}
                    </span>{" "}
                    {t("tools.csvWidget.mode.appendHint")}
                  </span>
                </label>
              </fieldset>

              <CsvStatementPanel
                value={statementForm}
                onChange={setStatementForm}
                currency={currency}
                outputMode={outputMode}
                reconciliation={result.statement}
                pending={inputPending}
                statementReconciled={statementReconciled}
              />
            </>
          )}
        </div>

        <div className="tool-output-pane">
          {hasInput && (
            <p className="tool-result-summary" role="status">
              {t("tools.csvWidget.status.counts", {
                converted: result.converted.toLocaleString(locale),
                input: inputCount.toLocaleString(locale),
                skipped: skippedCount.toLocaleString(locale),
                excluded: result.excluded.toLocaleString(locale),
              })}
            </p>
          )}
          {inputPending && hasInput && (
            <p className="tool-status" role="status">
              {t("tools.csvWidget.status.pending")}
            </p>
          )}
          {splitBlocked && (
            <p className="tool-notice" role="status">
              {t("toolsCsvSplit.outputBlocked")}
            </p>
          )}
          {allExcluded && (
            <p className="tool-status" role="status">
              {t("tools.csvWidget.status.allExcluded")}
            </p>
          )}
          {categoryReview !== null && fallbackCategoryRows > 0 && (
            <p className="tool-status" role="status">
              {t("tools.csvWidget.categories.summary", {
                mapped: mappedCategoryRows.toLocaleString(locale),
                fallback: fallbackCategoryRows.toLocaleString(locale),
                account: counterAccount.trim() || DEFAULT_COUNTER_ACCOUNT,
              })}
            </p>
          )}
          <ConverterOutput
            keyPrefix="tools.csvWidget"
            toolName="csv_to_beancount"
            filename={filename}
            output={outputText}
            converted={outputConverted}
            presetSlug={presetSlug}
            trackingDetails={trackingDetails}
            onArtifactAction={
              splitInOutput
                ? (action) =>
                    trackCsvSplitAction(action, presetSlug, result.outputMode)
                : undefined
            }
            afterOutput={
              outputReady ? (
                <div className="tool-notes">
                  <h3 className="tool-notes-heading">
                    {t("tools.csvWidget.next.title")}
                  </h3>
                  <p>
                    {t(
                      statementReconciled
                        ? result.outputMode === "complete"
                          ? "tools.csvWidget.next.reconciledComplete"
                          : "tools.csvWidget.next.reconciledAppend"
                        : result.outputMode === "complete"
                          ? "tools.csvWidget.next.complete"
                          : "tools.csvWidget.next.append",
                    )}
                  </p>
                  <p>
                    <LocaleLink
                      to="/docs/Basics/getting-started"
                      className="company-text-link"
                    >
                      {t("tools.csvWidget.next.docs")}
                    </LocaleLink>{" "}
                    <StartFreeLink
                      surface={ctaSurface}
                      onClick={() =>
                        trackToolUse(
                          "csv_to_beancount",
                          "cta",
                          presetSlug,
                          trackingDetails,
                        )
                      }
                    />
                    <span className="sr-only">
                      {t("tools.csvWidget.next.product")}
                    </span>
                  </p>
                </div>
              ) : undefined
            }
          />
          {deferredText.trim().length > 0 && blockingErrors.length > 0 && (
            <div className="tool-notes">
              <h3 className="tool-notes-heading">
                {t("tools.csvWidget.errors.blockedTitle")}
              </h3>
              <ul className="tool-notes-list">
                {blockingErrors.slice(0, 12).map((error, index) => (
                  <li key={`blocked-${index}`}>
                    {translatedCsvReason(t, error.reason)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {deferredText.trim().length > 0 && rowErrors.length > 0 && (
            <div className="tool-notes">
              <h3 className="tool-notes-heading">
                {t("tools.csvWidget.errors.title")}
              </h3>
              <ul className="tool-notes-list">
                {rowErrors.slice(0, 12).map((error, index) => (
                  <li key={`${error.line}-${index}`}>
                    {t("tools.csvWidget.errors.line", {
                      line: error.line.toLocaleString(locale),
                      reason: translatedCsvReason(t, error.reason),
                    })}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasInput && (
            <HorizontalScrollRegion
              label={t("tools.csvWidget.preview.title")}
              hint={t("horizontalScroll.hint")}
              className="tool-source-preview"
            >
              <table dir="ltr">
                <caption dir="auto">
                  {t("tools.csvWidget.preview.title")}
                </caption>
                <thead>
                  <tr>
                    {columnOptions.map((column) => (
                      <th key={column.value} scope="col" dir="auto">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsed.rows.slice(0, PREVIEW_ROWS).map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex} dir="auto">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </HorizontalScrollRegion>
          )}
        </div>
      </div>
    </section>
  );
}
